import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy, ElementRef, ViewChild, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

export interface ISimSnapshot {
  zaman: number;
  parcacikSayisi: number;
  dolumYuzdesi: number;
  ortSicaklik: number;
}

export interface ITimelineOlay {
  zaman: number;
  tip: 'dolum_basladi' | 'dolum_50' | 'dolum_tamamlandi' | 'katilasma_basladi' | 'hot_spot' | 'kusur';
  aciklama: string;
  renk: string;
}

const OLAY_RENKLERI: Record<string, string> = {
  'dolum_basladi': '#4ecca3',
  'dolum_50': '#ffa500',
  'dolum_tamamlandi': '#1890ff',
  'katilasma_basladi': '#a855f7',
  'hot_spot': '#ff4d4f',
  'kusur': '#ef4444',
  'baslangic': '#4ecca3',
  'kritik': '#ffa500',
  'uyari': '#ff4d4f',
  'son': '#1890ff'
};

@Component({
  selector: 'fiko-sph-timeline',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzSelectModule,
    NzSliderModule,
    NzIconModule,
    NzDropdownModule,
    NzMenuModule,
    NzBadgeModule,
    NzTooltipModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './fiko-sph-timeline.component.html',
  styleUrls: ['./fiko-sph-timeline.component.less']
})
export class FikoSPHTimelineComponent implements OnChanges {
  @Input() simZaman: number = 0;
  @Input() simHizi: number = 1.0;
  @Input() durum: string = 'bosta';
  @Input() maxZaman: number = 100;
  @Input() olayListesi: ITimelineOlay[] = [];

  @Output() hizDegisti = new EventEmitter<number>();
  @Output() senaryoSecildi = new EventEmitter<any>();
  @Output() zamanDegisti = new EventEmitter<number>();

  @ViewChild('progressBar', { static: true }) progressBarRef: ElementRef<HTMLDivElement>;

  zamanYuzdesi: number = 0;
  scrubbing = false;
  private frameCounter = 0;
  private readonly SNAPSHOT_INTERVAL = 30;

  snapshots: Map<number, ISimSnapshot> = new Map();

  hizSecenekleri = [
    { deger: 0.25, etiket: '0.25x' },
    { deger: 0.5, etiket: '0.5x' },
    { deger: 1.0, etiket: '1x' },
    { deger: 2.0, etiket: '2x' },
    { deger: 4.0, etiket: '4x' }
  ];

  senaryolar = [
    { id: 'altan-dokum', ad: 'Altan Dokum', aciklama: 'Standart altan dokum senaryosu', ikon: 'arrow-down' },
    { id: 'usten-dokum', ad: 'Usten Dokum', aciklama: 'Usten dokum senaryosu', ikon: 'arrow-up' },
    { id: 'coklu-giris', ad: 'Coklu Giris', aciklama: 'Birden fazla giris noktasi', ikon: 'branches' },
    { id: 'pulse', ad: 'Pulse Dokum', aciklama: 'Darbe seklinde dokum', ikon: 'stock' },
    { id: 'ozel', ad: 'Ozel Senaryo', aciklama: 'Kullanici tanimli senaryo', ikon: 'setting' }
  ];

  seciliSenaryo: any = this.senaryolar[0];

  olaylar: (ITimelineOlay | any)[] = [
    { zaman: 10, tip: 'baslangic', etiket: 'Dokum Basladi', aciklama: 'Dokum Basladi' },
    { zaman: 25, tip: 'kritik', etiket: 'Kalip %50 Doldu', aciklama: 'Kalip %50 Doldu' },
    { zaman: 45, tip: 'uyari', etiket: 'Sicaklik Artisi', aciklama: 'Sicaklik Artisi' },
    { zaman: 80, tip: 'son', etiket: 'Dokum Tamamlandi', aciklama: 'Dokum Tamamlandi' }
  ];

  private _boundMouseMove = this.onScrubMove.bind(this);
  private _boundMouseUp = this.onScrubEnd.bind(this);

  constructor(private ngZone: NgZone) {}

  get tumOlaylar(): (ITimelineOlay | any)[] {
    return this.olayListesi.length ? this.olayListesi : this.olaylar;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['simZaman'] && !this.scrubbing) {
      this.zamanYuzdesiGuncelle();
      this.frameCounter++;
      if (this.frameCounter % this.SNAPSHOT_INTERVAL === 0) {
        this.otomatikSnapshotAl();
      }
    }
  }

  private zamanYuzdesiGuncelle(): void {
    this.zamanYuzdesi = this.maxZaman > 0
      ? Math.min((this.simZaman / this.maxZaman) * 100, 100)
      : 0;
  }

  snapshotAl(snapshot: ISimSnapshot): void {
    const key = Math.round(snapshot.zaman * 10) / 10;
    this.snapshots.set(key, { ...snapshot });
  }

  private otomatikSnapshotAl(): void {
    const snap: ISimSnapshot = {
      zaman: this.simZaman,
      parcacikSayisi: 0,
      dolumYuzdesi: this.zamanYuzdesi,
      ortSicaklik: 0
    };
    this.snapshotAl(snap);
  }

  enYakinSnapshotBul(zaman: number): ISimSnapshot | null {
    if (this.snapshots.size === 0) return null;
    let enYakin: ISimSnapshot | null = null;
    let enKucukFark = Infinity;
    this.snapshots.forEach(snap => {
      const fark = Math.abs(snap.zaman - zaman);
      if (fark < enKucukFark) {
        enKucukFark = fark;
        enYakin = snap;
      }
    });
    return enYakin;
  }

  snapshotTemizle(): void {
    this.snapshots.clear();
    this.frameCounter = 0;
  }

  onScrubStart(event: MouseEvent): void {
    event.preventDefault();
    this.scrubbing = true;
    this.zamanHesaplaVeEmit(event);
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this._boundMouseMove);
      document.addEventListener('mouseup', this._boundMouseUp);
    });
  }

  private onScrubMove(event: MouseEvent): void {
    if (!this.scrubbing) return;
    this.zamanHesaplaVeEmit(event);
  }

  private onScrubEnd(): void {
    this.scrubbing = false;
    document.removeEventListener('mousemove', this._boundMouseMove);
    document.removeEventListener('mouseup', this._boundMouseUp);
  }

  private zamanHesaplaVeEmit(event: MouseEvent): void {
    const bar = this.progressBarRef?.nativeElement;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const oran = x / rect.width;
    const yeniZaman = oran * this.maxZaman;
    this.zamanYuzdesi = oran * 100;
    this.ngZone.run(() => {
      this.zamanDegisti.emit(Math.round(yeniZaman * 100) / 100);
    });
  }

  olayaAtla(olay: ITimelineOlay | any): void {
    this.zamanDegisti.emit(olay.zaman);
  }

  hizSec(hiz: number): void {
    this.hizDegisti.emit(hiz);
  }

  hizAyarla(hiz: number): void {
    this.hizDegisti.emit(hiz);
  }

  senaryoSec(senaryo: any): void {
    this.seciliSenaryo = senaryo;
    this.senaryoSecildi.emit(senaryo);
  }

  zamanFormatla(saniye: number): string {
    const dakika = Math.floor(saniye / 60);
    const sn = Math.floor(saniye % 60);
    const ms = Math.floor((saniye % 1) * 100);
    return `${dakika.toString().padStart(2, '0')}:${sn.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  }

  olayPozisyonuAl(zamanDegeri: number): string {
    return this.maxZaman > 0
      ? ((zamanDegeri / this.maxZaman) * 100) + '%'
      : '0%';
  }

  olayRengiAl(tip: string): string {
    return OLAY_RENKLERI[tip] || '#a0a0a0';
  }

  get snapshotEntries(): [number, ISimSnapshot][] {
    return Array.from(this.snapshots.entries());
  }

  olayTrackBy(_: number, olay: any): number {
    return olay.zaman;
  }

  snapshotTrackBy(_: number, entry: [number, ISimSnapshot]): number {
    return entry[0];
  }

  olayIkonuAl(tip: string): string {
    const ikonlar: Record<string, string> = {
      'dolum_basladi': 'play-circle',
      'dolum_50': 'percentage',
      'dolum_tamamlandi': 'check-circle',
      'katilasma_basladi': 'experiment',
      'hot_spot': 'fire',
      'kusur': 'warning',
      'baslangic': 'play-circle',
      'kritik': 'warning',
      'uyari': 'fire',
      'son': 'check-circle'
    };
    return ikonlar[tip] || 'info-circle';
  }
}
